import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { MobileAppSubpage } from '../mobile-store/mobile.data';
import { mobileAppSubpageReselector } from '../mobile-store/mobile.app.routing.reselector';
import { MobileAppWrapper } from '../mobile.app.wrapper.component';
import { AppNotificationBase, JoiApp, JoiAppData, JoiAppInfo } from 'globals/store/app.data';
import { mobileAppSurveysReselector, onRightAppReselector } from '../mobile-store/mobile.app.reselector';
import { FontCSS, getJoiTemplate } from 'globals/store/publish.data';
import { sleepForMS } from '../../../helpers/helpers';
import { MobileAppActions, mobileAppFeature } from '../mobile-store/mobile.app.era';
import { MobileAppTopNavComponent } from './mobile-app-top-nav.component';
import { SocialLinksComponent } from '../../social-links/social-links.component';
import { FooterLinksComponent } from '../../branding/footer-links.component';
import { IconComponent } from '../../icon/icon.component';
import { CommonModule } from '@angular/common';
import { LoaderComponent } from '../../loader/loader.component';

@Component({
    selector: 'mobile-app-nav-wrapper',
    template: `
<loader [icon]="'button_wheel'" *ngIf="!onRightApp || !allowedToLoad"></loader>
<div class="branding-wrapper" *ngIf="onRightApp && { template: app?.brandingData?.template || joiTemplate, branding: app?.brandingData } as vars"
  [class.loaded]="allowedToLoad"
  [style.--pagebackground]="vars.template.pagebackground"
  [style.--font]="FontCSS[vars.template.font]"
  [style.--header-font]="FontCSS[vars.template.headerFont]"
  [style.--primary]="vars.template.primary"
  [style.--primaryhover]="vars.template.primaryhover"
  [style.--link]="vars.template.link"
  [style.--contentbg]="vars.template.contentbg"
  [style.--contentbgoutline]="vars.template.contentbgoutline"
  [style.--innercontentbg]="vars.template.innercontentbg"
  [style.--innercontentbgoutline]="vars.template.innercontentbgoutline"
  >
  <mobile-app-top-nav *ngIf="!!topPagesBySubpage[subPage]"
    [subPages]="topPagesBySubpage[subPage]" 
    [iconMap]="pageIcons" 
    [names]="pageNames" 
    [selectedSubpage]="subPage"
    (clickSubpage)="changeSubpage($event)"  
  ></mobile-app-top-nav>
  <div class="body">
    <img #header class="app banner header" *ngIf="!!vars.branding?.header?.mobileLink" [class.hidden]="!brandingPages.has(subPage)" [src]="vars.branding.header.mobileLink" />
    <router-outlet></router-outlet>
    <div style="flex:1;"></div>
    <social-links *ngIf="brandingPages.has(subPage) && !!vars.branding?.social?.social.length" [socialLinks]="vars.branding?.social?.social"></social-links>
    <footer-links *ngIf="brandingPages.has(subPage) && !!vars.branding?.footerLinks?.links?.length" [footerLinks]="vars.branding?.footerLinks"></footer-links>
    <img #footer class="app banner footer" *ngIf="!!vars.branding?.footer?.mobileLink" [class.hidden]="!brandingPages.has(subPage)" [src]="vars.branding.footer.mobileLink" />
  </div>
  <div class="menu">
    <div class="sub-page smooth" *ngFor ="let sub of subPages" [class.active]="sub === subPage" (click)="changeSubpage(sub)">
      <icon [icon]="pageIcons[sub]"><div class="dot" *ngIf="dottedSubPages.has(sub)"></div></icon>
    </div>
  </div>
</div>
`,
    styleUrls: ["./mobile-app-nav-wrapper.scss"],
    standalone: true,
    imports: [MobileAppTopNavComponent, RouterOutlet, SocialLinksComponent, FooterLinksComponent, IconComponent, CommonModule, LoaderComponent]
})
export class MobileAppNavWrapperComponent extends MobileAppWrapper<MobileAppNavWrapperComponent> implements OnInit {
  @ViewChild('header') header: ElementRef<HTMLImageElement>;
  @ViewChild('footer') footer: ElementRef<HTMLImageElement>;
  headerLoaded: boolean
  footerLoaded: boolean
  allowedToLoad: boolean

  subPages: MobileAppSubpage[] = ['home', 'notifications', 'feedback', 'options']
  brandingPages: Set<MobileAppSubpage> = new Set(['home'])
  dottedSubPages: Set<MobileAppSubpage> = new Set()
  appInfo: JoiAppInfo
  appData: JoiAppData
  app: JoiApp
  subPage: MobileAppSubpage
  topPagesBySubpage: { [s in MobileAppSubpage]: MobileAppSubpage[] } = {
    home: null,
    notifications: ['notifications'],
    feedback: ['feedback'],
    options: null,
    program: ['program', 'agenda'],
    agenda: ['program', 'agenda'],
    privacy: ['privacy'],
    performers: ['performers'],
    sponsors: ['sponsors'],
    exhibitors: ['exhibitors'],
    URL: null
  }
  joiTemplate = getJoiTemplate()
  FontCSS = FontCSS
  appId: string
  shownNotification: AppNotificationBase
  onRightApp: boolean

  loadedImage(type: 'header' | 'footer') {
    switch (type) {
      case 'header':
        this.headerLoaded = true
        break
      case 'footer':
        this.footerLoaded = true
        break
    }
    if (this.headerLoaded && this.footerLoaded) this.allowedToLoad = true
  }

  async giveTimeForImages() {
    await sleepForMS(2000)  // need time for images to be able to be loaded in template before checking if they're complete

    if (!this.app?.brandingData?.header?.mobileLink || this.header.nativeElement.complete) this.loadedImage('header')
    else this.header.nativeElement.addEventListener('load', () => this.loadedImage('header'))

    if (!this.app?.brandingData?.footer?.mobileLink || this.footer.nativeElement.complete) this.loadedImage('footer')
    else this.footer.nativeElement.addEventListener('load', () => this.loadedImage('footer')) 

    await sleepForMS(5000) // in case images don't load / network error / something else, show app in 5 more seconds
    this.allowedToLoad = true
  }

  constructor(private route: ActivatedRoute) {
    super()
    this.selectProp(mobileAppSubpageReselector, null, 'subPage')
    this.selectProp(onRightAppReselector, null, 'onRightApp')
    this.subProp(this.store.select(mobileAppFeature.selectAppInfo), 'appInfo')
    this.subProp(this.store.select(mobileAppFeature.selectAppData), 'appData')
    this.subProp(this.store.select(mobileAppFeature.selectShownNotification), 'shownNotification')
    this.subCallback(this.store.select(mobileAppFeature.selectApp), app => {
      this.app = app
      if (!this.allowedToLoad && !!app) this.giveTimeForImages()
    })
    this.selectCallback(mobileAppSurveysReselector, null, ({ activeSurveys }) => {
      if (activeSurveys.length) this.dottedSubPages.add('feedback')
      else this.dottedSubPages.delete('feedback')
    })
  }

  ngOnInit() {
    this.route.paramMap.subscribe(paramMap => {
      this.appId = paramMap.get('id')
      console.log(`APP ID: ${this.appId}`)
      this.store.dispatch(MobileAppActions.loadAppId({ payload: this.appId }))
    })
  }

  openNotificationLink() {
    this.mobileAppService.openNotificationLink(this.shownNotification)
  }

  closeNotification() {
    this.mobileAppService.closeNotification(this.shownNotification)
  }

  changeSubpage(subPage: MobileAppSubpage, appId?: string) {
    this.mobileAppService.changeSubpage(subPage, appId)
  }
}